import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  buildAttributeCacheKey,
  buildCacheContext,
  buildFieldValuesFromAttributes,
  loadAttributeCache,
  matchesCacheContext,
  saveAttributeCache,
  serializeSelectedAttributes,
} from "../../../../utils/sendProductBulkAttributeCache";

const AttributeSelectionStep = ({
  setSelectedAttributes,
  onBack,
  onComplete,
  loading,
  selectedPlace,
  accountMarketpleacesName,
  categoryAttribute,
  selectedCategory,
  selectedPlacesCategory,
  userId,
}) => {
  const [outWardCategoryAttribute, setOutWardCategoryAttribute] = useState([]);
  const [fieldValues, setFieldValues] = useState({});
  const [cachedAttributes, setCachedAttributes] = useState(null);
  const [cacheApplied, setCacheApplied] = useState(false);

  const cacheKey = useMemo(
    () => buildAttributeCacheKey(userId, selectedPlace, selectedCategory),
    [userId, selectedPlace, selectedCategory]
  );

  useEffect(() => {
    if (categoryAttribute?.length > 0) {
      setOutWardCategoryAttribute(categoryAttribute.map((item) => item));
    } else {
      setOutWardCategoryAttribute([]);
    }
    setFieldValues({});
    setCacheApplied(false);
  }, [categoryAttribute]);

  useEffect(() => {
    if (!cacheKey) {
      setCachedAttributes(null);
      return;
    }
    const cached = loadAttributeCache(cacheKey);
    if (
      cached?.selectedAttributes?.length &&
      matchesCacheContext(
        cached.context,
        selectedPlace,
        selectedCategory,
        selectedPlacesCategory
      )
    ) {
      setCachedAttributes(cached.selectedAttributes);
    } else {
      setCachedAttributes(null);
    }
  }, [
    cacheKey,
    selectedPlace,
    selectedCategory,
    selectedPlacesCategory,
  ]);

  const updatedAttributes = useMemo(
    () =>
      outWardCategoryAttribute?.map((item) => ({
        ...item,
        attribute: { ...item.attribute, varianter: item.varianter },
      })) ?? [],
    [outWardCategoryAttribute]
  );

  const syncSelectedAttributes = useCallback(
    (nextFieldValues) => {
      const entries = updatedAttributes
        .map((item) => {
          const attrId = item?.attribute?.id;
          if (!attrId) return null;
          const value = nextFieldValues[attrId];
          if (value == null || value === "") return null;
          return { attribute: item.attribute, value };
        })
        .filter(Boolean);
      setSelectedAttributes(entries);
    },
    [updatedAttributes, setSelectedAttributes]
  );

  const handleSelect = (attribute, value) => {
    const attrId = attribute?.id;
    if (!attrId) return;

    const nextFieldValues = { ...fieldValues };
    if (value != null && value !== "") {
      nextFieldValues[attrId] = value;
    } else {
      delete nextFieldValues[attrId];
    }
    setFieldValues(nextFieldValues);
    syncSelectedAttributes(nextFieldValues);
  };

  const handleApplyCachedValues = () => {
    if (!cachedAttributes?.length) return;
    const nextFieldValues = buildFieldValuesFromAttributes(
      updatedAttributes,
      cachedAttributes
    );
    setFieldValues(nextFieldValues);
    setSelectedAttributes(serializeSelectedAttributes(cachedAttributes));
    setCacheApplied(true);
  };

  const handleComplete = () => {
    if (cacheKey && selectedAttributesFromFields().length > 0) {
      saveAttributeCache(
        cacheKey,
        buildCacheContext(
          selectedPlace,
          selectedCategory,
          selectedPlacesCategory
        ),
        selectedAttributesFromFields()
      );
    }
    onComplete();
  };

  const selectedAttributesFromFields = () =>
    updatedAttributes
      .map((item) => {
        const attrId = item?.attribute?.id;
        if (!attrId) return null;
        const value = fieldValues[attrId];
        if (value == null || value === "") return null;
        return { attribute: item.attribute, value };
      })
      .filter(Boolean);

  const showRememberButton =
    cachedAttributes?.length > 0 && !cacheApplied;

  const skipAttributes =
    selectedPlace?.type === "ecommerce" || selectedPlace?.name === "Pttavm";

  return (
    <Box sx={{ position: "relative", pb: 10 }}>
      {showRememberButton && (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            startIcon={<HistoryIcon />}
            onClick={handleApplyCachedValues}
            sx={{ borderRadius: 2 }}
          >
            Hatırlanan değerleri kullan (
            {cachedAttributes.length} özellik)
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
            Aynı pazaryeri ve kategori için son 1 saat içinde girdiğiniz
            özellikler.
          </Typography>
        </Box>
      )}

      {cacheApplied && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Hatırlanan özellik değerleri uygulandı. Gerekirse düzenleyebilirsiniz.
        </Alert>
      )}

      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="40vh"
          width="100%"
        >
          <CircularProgress />
        </Box>
      ) : skipAttributes ? (
        <Alert severity="warning" sx={{ width: "100%", mb: 2 }}>
          Seçilen pazaryeri için özellik seçimi gerekli değil
        </Alert>
      ) : !accountMarketpleacesName?.includes(selectedPlace?.name) ? (
        <Alert severity="warning" sx={{ width: "100%", mb: 2 }}>
          Seçilen pazaryeri için ayarlar eksik. Lütfen{" "}
          <a
            href="/Profile"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              textDecoration: "underline",
              cursor: "pointer",
              color: "blue",
            }}
          >
            profil
          </a>{" "}
          alanından gerekli bilgileri doldurunuz.
        </Alert>
      ) : categoryAttribute?.length > 0 ? (
        <Stack spacing={2}>
          {updatedAttributes
            ?.sort((a, b) => b.required - a.required)
            ?.map(
              (item) =>
                item?.attributeValues?.length > 0 && (
                  <Autocomplete
                    key={item?.attribute?.id}
                    disablePortal
                    options={item?.attributeValues}
                    value={fieldValues[item?.attribute?.id] ?? null}
                    getOptionLabel={(option) => option?.name || ""}
                    isOptionEqualToValue={(option, val) =>
                      (option?.id != null &&
                        val?.id != null &&
                        String(option.id) === String(val.id)) ||
                      (option?.name && option.name === val?.name)
                    }
                    onChange={(event, value) =>
                      handleSelect(item.attribute, value)
                    }
                    filterOptions={(options, { inputValue }) => {
                      const filteredOptions = options.filter((option) =>
                        option.name
                          .toLowerCase()
                          .includes(inputValue.toLowerCase())
                      );
                      return filteredOptions.slice(0, 20);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={
                          item.required
                            ? `${item?.attribute?.name} (${item?.attribute?.varianter ? "Varianter" : ""})`
                            : item?.attribute?.name
                        }
                        required={item.required}
                        InputLabelProps={{
                          style: {
                            color: item.required ? "red" : "inherit",
                          },
                        }}
                      />
                    )}
                  />
                )
            )}

          {updatedAttributes.map(
            (item) =>
              item?.attributeValues?.length === 0 && (
                <TextField
                  key={item?.attribute?.id ?? item?.attribute?.name}
                  id={`attr-${item?.attribute?.id}`}
                  label={
                    item.required
                      ? `${item?.attribute?.name} * (${item?.attribute?.varianter ? "Varianter" : ""})`
                      : item?.attribute?.name
                  }
                  variant="outlined"
                  required={item.required}
                  value={fieldValues[item?.attribute?.id] ?? ""}
                  sx={{ width: "100%" }}
                  onChange={(e) =>
                    handleSelect(item.attribute, e.target.value)
                  }
                />
              )
          )}
        </Stack>
      ) : (
        <Alert severity="warning" sx={{ width: "100%", mb: 2 }}>
          Seçilen pazaryerinde ürününüze ait kategori eşleşmiyor. Kategori
          sayfasından eşleştirme yapınız.
        </Alert>
      )}

      <Box display="flex" justifyContent="space-between" sx={{ mt: 2 }}>
        <Button
          variant="outlined"
          onClick={onBack}
          size="large"
          sx={{ px: 4, borderRadius: 2 }}
        >
          Geri
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleComplete}
          size="large"
          sx={{ px: 4, borderRadius: 2 }}
        >
          Devam
        </Button>
      </Box>
    </Box>
  );
};

export default AttributeSelectionStep;
